'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ScheduleFormData {
  serviceType: string;
  preferredDate: string;
  preferredTime: string;
  contactName: string;
  contactPhone: string;
  siteLocation: string;
  additionalNotes: string;
}

export default function MaintenanceScheduling() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<ScheduleFormData>({
    serviceType: '',
    preferredDate: '',
    preferredTime: '',
    contactName: '',
    contactPhone: '',
    siteLocation: '',
    additionalNotes: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const maintenanceServices = [
    'Telecom Tower Inspection',
    'Electrical System Audit',
    'Preventive Maintenance',
    'Equipment Testing',
    'Safety Compliance Check',
    'Performance Optimization',
  ];

  const timeSlots = [
    '09:00 AM - 12:00 PM',
    '12:00 PM - 03:00 PM',
    '03:00 PM - 06:00 PM',
    'Flexible Timing',
  ];

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    await new Promise((resolve) => setTimeout(resolve, 2000));

    setIsSubmitting(false);
    setSubmitStatus('success');

    setTimeout(() => {
      setFormData({
        serviceType: '',
        preferredDate: '',
        preferredTime: '',
        contactName: '',
        contactPhone: '',
        siteLocation: '',
        additionalNotes: '',
      });
      setSubmitStatus('idle');
    }, 3000);
  };

  if (!isHydrated) {
    return (
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-muted">
        <div className="max-w-4xl mx-auto">
          <div className="bg-card rounded-lg p-8 border border-border">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-muted rounded w-3/4"></div>
              <div className="h-4 bg-muted rounded w-1/2"></div>
              <div className="space-y-3 pt-6">
                <div className="h-12 bg-muted rounded"></div>
                <div className="h-12 bg-muted rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-muted">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-accent/10 rounded-full mb-4">
            <Icon name="CalendarDaysIcon" size={32} className="text-accent" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-headline font-bold text-textPrimary mb-4">
            Schedule Maintenance
          </h2>
          <p className="text-lg text-textSecondary max-w-2xl mx-auto">
            Book a preventive maintenance visit or schedule routine inspections for your infrastructure.
          </p>
        </div>

        <div className="bg-card rounded-lg p-8 border border-border shadow-lg">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="serviceType" className="block text-sm font-headline font-bold text-textPrimary mb-2">
                Maintenance Service <span className="text-destructive">*</span>
              </label>
              <div className="relative">
                <select
                  id="serviceType"
                  name="serviceType"
                  value={formData.serviceType}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-background border border-input rounded-lg text-textPrimary font-body focus:outline-none focus:ring-2 focus:ring-ring appearance-none"
                >
                  <option value="">Select maintenance service</option>
                  {maintenanceServices.map((service) => (
                    <option key={service} value={service}>
                      {service}
                    </option>
                  ))}
                </select>
                <Icon
                  name="ChevronDownIcon"
                  size={20}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-textSecondary pointer-events-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="preferredDate" className="block text-sm font-headline font-bold text-textPrimary mb-2">
                  Preferred Date <span className="text-destructive">*</span>
                </label>
                <input
                  type="date"
                  id="preferredDate"
                  name="preferredDate"
                  value={formData.preferredDate}
                  onChange={handleInputChange}
                  required
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 bg-background border border-input rounded-lg text-textPrimary font-body focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div>
                <label htmlFor="preferredTime" className="block text-sm font-headline font-bold text-textPrimary mb-2">
                  Preferred Time Slot <span className="text-destructive">*</span>
                </label>
                <div className="relative">
                  <select
                    id="preferredTime"
                    name="preferredTime"
                    value={formData.preferredTime}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-background border border-input rounded-lg text-textPrimary font-body focus:outline-none focus:ring-2 focus:ring-ring appearance-none"
                  >
                    <option value="">Select time slot</option>
                    {timeSlots.map((slot) => (
                      <option key={slot} value={slot}>
                        {slot}
                      </option>
                    ))}
                  </select>
                  <Icon
                    name="ChevronDownIcon"
                    size={20}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-textSecondary pointer-events-none"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="contactName" className="block text-sm font-headline font-bold text-textPrimary mb-2">
                  Contact Person <span className="text-destructive">*</span>
                </label>
                <input
                  type="text"
                  id="contactName"
                  name="contactName"
                  value={formData.contactName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-background border border-input rounded-lg text-textPrimary font-body focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Full name"
                />
              </div>

              <div>
                <label htmlFor="contactPhone" className="block text-sm font-headline font-bold text-textPrimary mb-2">
                  Contact Number <span className="text-destructive">*</span>
                </label>
                <input
                  type="tel"
                  id="contactPhone"
                  name="contactPhone"
                  value={formData.contactPhone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-background border border-input rounded-lg text-textPrimary font-body focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="+91 98765 43210"
                />
              </div>
            </div>

            <div>
              <label htmlFor="siteLocation" className="block text-sm font-headline font-bold text-textPrimary mb-2">
                Site Location <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                id="siteLocation"
                name="siteLocation"
                value={formData.siteLocation}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 bg-background border border-input rounded-lg text-textPrimary font-body focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="Complete site address"
              />
            </div>

            <div>
              <label htmlFor="additionalNotes" className="block text-sm font-headline font-bold text-textPrimary mb-2">
                Additional Notes
              </label>
              <textarea
                id="additionalNotes"
                name="additionalNotes"
                value={formData.additionalNotes}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-4 py-3 bg-background border border-input rounded-lg text-textPrimary font-body focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                placeholder="Any specific requirements or concerns..."
              ></textarea>
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-6 py-4 bg-accent text-accent-foreground rounded-lg font-headline font-bold text-lg shadow-lg hover:bg-accent/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
              >
                {isSubmitting ? (
                  <>
                    <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                    Scheduling...
                  </>
                ) : (
                  <>
                    <Icon name="CalendarDaysIcon" size={20} />
                    Schedule Maintenance
                  </>
                )}
              </button>
            </div>

            {submitStatus === 'success' && (
              <div className="bg-success/10 border border-success/30 rounded-lg p-4 flex items-start gap-3">
                <Icon name="CheckCircleIcon" size={24} className="text-success flex-shrink-0" />
                <div>
                  <h4 className="font-headline font-bold text-success mb-1">Maintenance Scheduled!</h4>
                  <p className="text-sm text-textSecondary">
                    Your maintenance visit has been scheduled. Our team will contact you 24 hours before the scheduled time to confirm.
                  </p>
                </div>
              </div>
            )}
          </form>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-card rounded-lg p-4 border border-border">
            <div className="flex items-center gap-3 mb-2">
              <Icon name="ShieldCheckIcon" size={20} className="text-success" />
              <h4 className="font-headline font-bold text-textPrimary text-sm">Certified Technicians</h4>
            </div>
            <p className="text-xs text-textSecondary">All maintenance performed by certified professionals</p>
          </div>

          <div className="bg-card rounded-lg p-4 border border-border">
            <div className="flex items-center gap-3 mb-2">
              <Icon name="DocumentTextIcon" size={20} className="text-primary" />
              <h4 className="font-headline font-bold text-textPrimary text-sm">Detailed Reports</h4>
            </div>
            <p className="text-xs text-textSecondary">Comprehensive maintenance reports after each visit</p>
          </div>

          <div className="bg-card rounded-lg p-4 border border-border">
            <div className="flex items-center gap-3 mb-2">
              <Icon name="ClockIcon" size={20} className="text-accent" />
              <h4 className="font-headline font-bold text-textPrimary text-sm">Flexible Scheduling</h4>
            </div>
            <p className="text-xs text-textSecondary">Schedule at your convenience, including weekends</p>
          </div>
        </div>
      </div>
    </section>
  );
}